// Small building blocks shared by the admin screens.

import React, { useEffect } from "react";
import { XMarkIcon, SparklesIcon } from "@heroicons/react/20/solid";
import { TagSuggestion } from "../../lib/autoTag";

export const inputClass =
  "w-full border border-neutral-300 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-neutral-900/20 focus:border-neutral-500";

export const primaryButton =
  "inline-flex items-center justify-center gap-2 bg-neutral-950 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export const secondaryButton =
  "inline-flex items-center justify-center gap-2 bg-white text-neutral-900 text-sm font-medium px-4 py-2 rounded-md border border-neutral-300 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

export const dangerButton =
  "inline-flex items-center justify-center gap-2 bg-white text-red-700 text-sm font-medium px-4 py-2 rounded-md border border-red-200 hover:bg-red-50 disabled:opacity-50 transition-colors";

export function Field({
  label,
  hint,
  error,
  children,
  htmlFor,
}: {
  label: string;
  hint?: React.ReactNode;
  error?: string;
  children: React.ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium text-neutral-800">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-red-600">{error}</p>
      ) : hint ? (
        <p className="text-xs text-neutral-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function Section({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white border border-neutral-200 rounded-lg p-5 md:p-6 flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold text-neutral-950">{title}</h2>
          {description && <p className="text-sm text-neutral-500 max-w-2xl">{description}</p>}
        </div>
        {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
      </div>
      {children}
    </section>
  );
}

export function StatusBadge({ status }: { status?: string }) {
  const published = status === "published";
  const legacy = !status;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
        published
          ? "bg-emerald-50 text-emerald-800"
          : legacy
            ? "bg-neutral-100 text-neutral-600"
            : "bg-amber-50 text-amber-800"
      }`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          published ? "bg-emerald-500" : legacy ? "bg-neutral-400" : "bg-amber-500"
        }`}
      />
      {published ? "Publiée" : legacy ? "Sans statut" : "Brouillon"}
    </span>
  );
}

export function ProgressBar({ value, label }: { value: number; label?: string }) {
  return (
    <div className="flex flex-col gap-1">
      {label && <span className="text-xs text-neutral-600">{label}</span>}
      <div className="w-full bg-neutral-200 rounded-full h-1.5 overflow-hidden">
        <div
          className="bg-neutral-900 h-1.5 rounded-full transition-[width] duration-200"
          style={{ width: `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Toggle chips for a taxonomy. Suggested values show a sparkle and their
 * reason on hover; admins accept a suggestion by clicking it.
 */
export function ChipSelector({
  options,
  selected,
  onChange,
  suggestions = [],
}: {
  options: string[];
  selected: string[];
  onChange: (next: string[]) => void;
  suggestions?: TagSuggestion[];
}) {
  const suggested = new Map(suggestions.map((s) => [s.value, s]));
  const extra = selected.filter((s) => !options.includes(s));
  return (
    <div className="flex flex-wrap gap-2">
      {[...options, ...extra].map((option) => {
        const isOn = selected.includes(option);
        const hint = suggested.get(option);
        return (
          <button
            type="button"
            key={option}
            title={hint ? `Suggestion : ${hint.reason}` : undefined}
            aria-pressed={isOn}
            onClick={() =>
              onChange(isOn ? selected.filter((s) => s !== option) : [...selected, option])
            }
            className={`inline-flex items-center gap-1 text-sm py-1.5 px-3 rounded-full border transition-colors ${
              isOn
                ? "bg-neutral-900 text-white border-neutral-900"
                : hint
                  ? "bg-violet-50 text-violet-900 border-violet-300 border-dashed hover:bg-violet-100"
                  : "bg-white text-neutral-800 border-neutral-300 hover:border-neutral-500"
            }`}
          >
            {hint && !isOn && <SparklesIcon className="w-3.5 h-3.5" aria-hidden="true" />}
            {option}
            {!options.includes(option) && <span className="text-xs opacity-60">(hors liste)</span>}
          </button>
        );
      })}
    </div>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[3000] bg-neutral-950/50 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      onMouseDown={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`bg-white rounded-lg shadow-xl w-full ${wide ? "max-w-5xl" : "max-w-lg"} flex flex-col max-h-[calc(100vh-2rem)]`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-neutral-500 hover:text-neutral-900" aria-label="Fermer">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-neutral-200 bg-neutral-50 rounded-b-lg flex flex-wrap justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
